import React, { useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  INSURANCE_POLICY_SOURCE_OTHER,
  INSURANCE_POLICY_STATUS_PENDING,
  UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
  UTILITY_TYPE_ID_INTERNET,
  UTILITY_TYPE_ID_LIABILITY_INSURANCE,
} from 'common/utility/constants';

import useBranch from 'client/hooks/useBranch';
import getDownloadUrl from 'client/actions/InsurancePolicy/getDownloadUrl';
import log from 'client/lib/log';

import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrIcon from 'client/components/SrIcon';
import SrS3FileDisplay from 'client/components/SrS3FileDisplay';
import SrDownloadButton from 'client/components/SrDownloadButton';
import SrUtilityDisableInternetConfirmationModal from 'client/components/SrUtilitySetup/SrUtilityDisableInternetConfirmationModal';

import './SrUtilityActivationStatusModalSection.less';

const DISPLAY_DATE_FORMAT = 'MMM D, YYYY';
const ACTIVATION_DATE_FORMAT = 'YYYY-MM-DD';

const SECTION_SUBTITLE_STATES = {
  ACTIVE: {
    label: 'Active',
    color: '#00A238',
  },
  INCOMPLETE: {
    label: 'Incomplete',
    color: '#E7711B',
  },
  DISABLED: {
    label: 'Disabled',
    color: '#E7711B',
  },
};

const REQUIRED_TYPE_BUTTON_STATES = {
  DISABLE: {
    label: 'Disable Utility',
    color: '#E7711B',
  },
  ENABLE: {
    label: 'Enable Utility',
    color: '#00A238',
  },
};

const getIsItemComplete = ({ activation, utilityTypeId }) => {
  const item = activation.items.find((item) => item.utilityTypeId === utilityTypeId);
  if (!item) {
    return false;
  }
  return [
    UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT,
    UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED,
  ].includes(item.status);
};

const activationHasUtilityType = ({ activation, utilityTypeId }) => {
  return activation.items.some((item) => item.utilityTypeId === utilityTypeId);
};

const checkLeaseStartDateWarning = ({ activationDate, leaseStartDate }) => {
  const formattedActivationDate = moment(activationDate).format(ACTIVATION_DATE_FORMAT);
  return new Date(formattedActivationDate) > new Date(leaseStartDate);
};
const isPreviewableFile = (url) => {
  if (!url) return false;
  const previewableExtensions = ['.pdf', '.jpg', '.jpeg', '.png', '.gif'];
  const lowerUrl = url.toLowerCase();
  return previewableExtensions.some((ext) => lowerUrl.endsWith(ext));
};

const SrUtilityActivationStatusModalSection = ({
  utilityType,
  utilityPartner,
  activation,
  leaseStartDate,
  onRequiredUtilityTypeToggle,
  onEditItem,
  onDocumentPreview,
}) => {
  const { dispatch } = useBranch({});
  const [confirmationModalArgs, setConfirmationModalArgs] = useState({
    displayConfirmationModal: false,
  });

  const activationItem = activation.items.find((item) => item.utilityTypeId === utilityType.id);

  const hasActivationItem = activationHasUtilityType({ activation, utilityTypeId: utilityType.id });
  const isItemComplete = getIsItemComplete({ activation, utilityTypeId: utilityType.id });
  const isInternetItem = utilityType.id === UTILITY_TYPE_ID_INTERNET;
  const isLiabilityInsuranceItem = utilityType.id === UTILITY_TYPE_ID_LIABILITY_INSURANCE;

  const subtitle = hasActivationItem
    ? isItemComplete
      ? SECTION_SUBTITLE_STATES.ACTIVE
      : SECTION_SUBTITLE_STATES.INCOMPLETE
    : SECTION_SUBTITLE_STATES.DISABLED;

  const requiredTypeButton = hasActivationItem
    ? REQUIRED_TYPE_BUTTON_STATES.DISABLE
    : REQUIRED_TYPE_BUTTON_STATES.ENABLE;

  const isDropout = activationItem?.status === UTILITY_ACTIVATION_ITEM_STATUS_DROPOUT;
  const isInternetDropout = isInternetItem && isDropout;

  const showLeaseStartDateWarning =
    activationItem?.activationDate &&
    checkLeaseStartDateWarning({
      activationDate: activationItem.activationDate,
      leaseStartDate,
    });

  const handleRequiredUtilityTypeToggle = () => {
    if (isInternetItem && hasActivationItem) {
      setConfirmationModalArgs({
        displayConfirmationModal: true,
        handleConfirmation: () => onRequiredUtilityTypeToggle(utilityType.id),
        closeModal: () => setConfirmationModalArgs({ displayConfirmationModal: false }),
      });
    } else {
      onRequiredUtilityTypeToggle(utilityType.id);
    }
  };

  const handleDownloadPolicyDocument = async (policyId) => {
    try {
      const result = await dispatch(getDownloadUrl, { policyId });
      if (!result || !result.downloadUrl) {
        log.error('Failed to get download URL for policy:', policyId);
        return;
      }
      window.open(result.downloadUrl, '_blank');
    } catch (error) {
      log.error('Error downloading policy document:', error);
    }
  };

  const isFilePreviewable =
    activationItem?.proofOfPurchaseUrl && isPreviewableFile(activationItem.proofOfPurchaseUrl);

  return (
    <div styleName="SrUtilityActivationStatusModalSection">
      <div styleName="header">
        <SrFlex>
          {isLiabilityInsuranceItem ? (
            hasActivationItem && isItemComplete ? (
              <img
                styleName="icon"
                title="Utility Icon"
                alt="Utility Icon"
                src="/images/emoji-checkmark-green-3x.png"
              />
            ) : (
              <div styleName="icon" style={{ fontSize: '24px', lineHeight: '28px' }}>
                🏠
              </div>
            )
          ) : (
            <img
              styleName="icon"
              title="Utility Icon"
              alt="Utility Icon"
              src={
                hasActivationItem && isItemComplete
                  ? '/images/emoji-checkmark-green-3x.png'
                  : utilityType.iconUrl
              }
            />
          )}
          <div styleName="title">
            {isLiabilityInsuranceItem ? 'Insurance' : utilityType.label}:&nbsp;
          </div>
          <div styleName="title" style={{ color: subtitle.color }}>
            {subtitle.label}
          </div>
        </SrFlex>
        <div
          styleName="toggleButton"
          style={{ color: requiredTypeButton.color }}
          onClick={handleRequiredUtilityTypeToggle}
        >
          {requiredTypeButton.label}
        </div>
      </div>

      {activationItem && isLiabilityInsuranceItem && (
        <div styleName="content">
          {activationItem.insurancePolicy?.source === INSURANCE_POLICY_SOURCE_OTHER ? (
            // External provider view
            <div styleName="section">
              <div styleName="label">What provider did they choose?</div>
              <SrBox>
                <div styleName="value">
                  External Provider
                  {activationItem.insurancePolicy?.urlFullPolicyDecPage && (
                    <>
                      {' - '}
                      <span
                        onClick={() =>
                          handleDownloadPolicyDocument(activationItem.insurancePolicy.id)
                        }
                        styleName="editButton"
                        style={{ textDecoration: 'underline', cursor: 'pointer' }}
                      >
                        View Proof of Purchase
                      </span>
                    </>
                  )}
                </div>
              </SrBox>
            </div>
          ) : (
            // Beagle provider view
            <>
              {activationItem.status !== UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED ? (
                // Not completed state - only show provider as "pending"
                <div styleName="section">
                  <div styleName="label">What provider did they choose?</div>
                  <SrBox>
                    <div styleName="value">Pending</div>
                  </SrBox>
                </div>
              ) : (
                // Completed state - show all details
                <>
                  <div styleName="section">
                    <div styleName="label">Status</div>
                    <SrBox>
                      <div styleName="value">
                        {activationItem.insurancePolicy?.status || INSURANCE_POLICY_STATUS_PENDING}
                      </div>
                    </SrBox>
                  </div>

                  <div styleName="section">
                    <div styleName="label">What provider did they choose?</div>
                    <SrBox>
                      <div styleName="value">
                        SimpleCover Insurance™️
                        {activationItem.insurancePolicy?.urlFullPolicyDecPage && (
                          <>
                            {' - '}
                            <span
                              onClick={() =>
                                handleDownloadPolicyDocument(activationItem.insurancePolicy.id)
                              }
                              styleName="editButton"
                              style={{ textDecoration: 'underline', cursor: 'pointer' }}
                            >
                              View Policy Document
                            </span>
                          </>
                        )}
                      </div>
                    </SrBox>
                  </div>

                  {activationItem.insurancePolicy?.policyNumber && (
                    <div styleName="section">
                      <div styleName="label">Policy ID</div>
                      <SrBox>
                        <div styleName="value">{activationItem.insurancePolicy.policyNumber}</div>
                      </SrBox>
                    </div>
                  )}

                  <div styleName="section">
                    <SrFlex justifyContent="space-between">
                      <div style={{ flex: 1, marginRight: '20px' }}>
                        <div styleName="label">Liability amount</div>
                        <SrBox>
                          <div styleName="value">
                            {(() => {
                              const amount = Number(
                                activationItem.insurancePolicy?.personalLiabilityCoverage,
                              );
                              return !isNaN(amount) ? `$${amount.toLocaleString()}` : 'N/A';
                            })()}
                          </div>
                        </SrBox>
                      </div>
                      <div style={{ flex: 1 }}>
                        <div styleName="label">Personal property amount</div>
                        <SrBox>
                          <div styleName="value">
                            {(() => {
                              const amount = Number(
                                activationItem.insurancePolicy?.personalContentsCoverage,
                              );
                              return !isNaN(amount) ? `$${amount.toLocaleString()}` : 'N/A';
                            })()}
                          </div>
                        </SrBox>
                      </div>
                    </SrFlex>
                  </div>

                  <div styleName="section">
                    <SrFlex justifyContent="space-between">
                      <div style={{ flex: 1, marginRight: '20px' }}>
                        <div styleName="label">Policy start date</div>
                        <SrBox>
                          <div styleName="value">
                            {activationItem.insurancePolicy?.coverageStartDate
                              ? moment(activationItem.insurancePolicy.coverageStartDate).format(
                                  'MM/DD/YYYY',
                                )
                              : 'N/A'}
                          </div>
                        </SrBox>
                      </div>
                      {activationItem.insurancePolicy?.coverageEndDate && (
                        <div style={{ flex: 1 }}>
                          <div styleName="label">Policy end date</div>
                          <SrBox>
                            <div styleName="value">
                              {moment(activationItem.insurancePolicy.coverageEndDate).format(
                                'MM/DD/YYYY',
                              )}
                            </div>
                          </SrBox>
                        </div>
                      )}
                    </SrFlex>
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {activationItem && !isLiabilityInsuranceItem && (
        <div styleName="content">
          <div styleName="section">
            <div styleName="label">What provider did they choose?</div>
            <SrBox>
              <div
                styleName={`value ${
                  !isInternetDropout && !activationItem.provider?.title ? 'incomplete' : ''
                }`}
              >
                {isInternetDropout
                  ? `Unknown (renter purchased ${activationItem.type.label.toLowerCase()} ` +
                    'outside of this tool)'
                  : activationItem.provider?.title || 'Incomplete'}
                {!isInternetDropout && (
                  <div styleName="editButton" onClick={() => onEditItem(activationItem)}>
                    &nbsp;- Edit Utility
                  </div>
                )}
              </div>
            </SrBox>
          </div>

          <div styleName="section">
            <div styleName="label">Account ID</div>
            <SrBox>
              <div
                styleName={`value ${
                  !isInternetDropout && !activationItem.accountNumber ? 'incomplete' : ''
                }`}
              >
                {isInternetDropout
                  ? `Unknown (renter purchased ${activationItem.type.label.toLowerCase()} ` +
                    'outside of this tool)'
                  : activationItem.accountNumber || 'Incomplete'}
                {activationItem.verifiedOrder &&
                  activationItem.status === UTILITY_ACTIVATION_ITEM_STATUS_COMPLETED && (
                    <span style={{ marginLeft: 10 }}>
                      (
                      <img
                        src="/images/star-verified-utility-setup.svg"
                        alt="Verified"
                        style={{ width: 14, height: 14, verticalAlign: 'middle', marginRight: 3 }}
                      />
                      {'Verified utility setup '}
                      <a
                        href="https://utility-setup.frontkb.com/en/articles/11156353"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: '#2563EB', textDecoration: 'underline' }}
                      >
                        Learn More
                      </a>
                      )
                    </span>
                  )}
                {!isInternetDropout && (
                  <div styleName="editButton" onClick={() => onEditItem(activationItem)}>
                    &nbsp;- Edit Utility
                  </div>
                )}
              </div>
            </SrBox>
          </div>

          {!isInternetDropout && (
            <div styleName="section">
              <div styleName="label">Activation date</div>
              <SrBox>
                <div styleName={`value ${!activationItem.activationDate ? 'incomplete' : ''}`}>
                  {activationItem.activationDate
                    ? moment(activationItem.activationDate).format(DISPLAY_DATE_FORMAT)
                    : 'Incomplete'}
                  <div styleName="editButton" onClick={() => onEditItem(activationItem)}>
                    &nbsp;-{' '}
                    {isInternetItem ? 'Mark as "purchased outside of this tool"' : 'Edit Utility'}
                  </div>
                  {showLeaseStartDateWarning && (
                    <span styleName="warning">
                      <SrIcon className="icon warning-icon" display="inlineBlock" />
                      &nbsp;Begins after the lease start date.
                    </span>
                  )}
                </div>
              </SrBox>
            </div>
          )}

          {activationItem.proofOfPurchaseUrl && (
            <div styleName="section">
              <div styleName="label">
                <SrFlex alignItems="center" justifyContent="flex-start">
                  <span>Confirmation File</span>
                  <SrBox marginLeft="8px">
                    <SrDownloadButton
                      fileUrl={activationItem.proofOfPurchaseUrl}
                      label="Download File"
                      fontSize="6px"
                    />
                  </SrBox>
                </SrFlex>
              </div>
              <SrBox styleName="screenshot">
                {isFilePreviewable && (
                  <>
                    <div styleName="previewText" onClick={() => onDocumentPreview(activationItem)}>
                      (Click to preview)
                    </div>
                    <SrS3FileDisplay
                      url={activationItem.proofOfPurchaseUrl}
                      maxHeight="280px"
                      withRotateButton
                    />
                  </>
                )}
              </SrBox>
            </div>
          )}
        </div>
      )}
      {confirmationModalArgs.displayConfirmationModal && (
        <SrUtilityDisableInternetConfirmationModal
          utilityPartner={utilityPartner}
          closeModal={confirmationModalArgs.closeModal}
          modalArgs={confirmationModalArgs}
        />
      )}
    </div>
  );
};

SrUtilityActivationStatusModalSection.propTypes = {
  utilityType: PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    iconUrl: PropTypes.string.isRequired,
  }).isRequired,
  utilityPartner: PropTypes.object,
  activation: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        utilityTypeId: PropTypes.string.isRequired,
        status: PropTypes.string,
        type: PropTypes.shape({
          label: PropTypes.string.isRequired,
        }),
        provider: PropTypes.shape({
          title: PropTypes.string,
        }),
        accountNumber: PropTypes.string,
        activationDate: PropTypes.string,
        proofOfPurchaseUrl: PropTypes.string,
        insurancePolicy: PropTypes.shape({
          id: PropTypes.string,
          source: PropTypes.string,
          policyNumber: PropTypes.string,
          status: PropTypes.string,
          coverageStartDate: PropTypes.string,
          coverageEndDate: PropTypes.string,
          personalLiabilityCoverage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          personalContentsCoverage: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          urlFullPolicyDecPage: PropTypes.string,
        }),
      }),
    ).isRequired,
  }).isRequired,
  leaseStartDate: PropTypes.string.isRequired,
  onRequiredUtilityTypeToggle: PropTypes.func.isRequired,
  onEditItem: PropTypes.func.isRequired,
  onDocumentPreview: PropTypes.func.isRequired,
};

export default SrUtilityActivationStatusModalSection;
